import { useState, useEffect, useContext } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ModalKey, ModalContext } from 'components/Modals/ModalContext'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { useSelector, useDispatch } from 'react-redux'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { useGetAthletes } from 'redux/customHooks/useGetAthletes'
import { resetOrganizationState } from 'redux/slices/organization'
import { CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import moment from 'moment'

import ScreenFrame from 'components/Frame/ScreenFrame'
import ContentFrame from 'components/Frame/ContentFrame'
import TrainingPeaksToKinesisAthleteMappingListItem from 'components/Onboarding/TrainingPeaksToKinesisAthleteMappingListItem'
import SolidButton from 'components/Buttons/SolidButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'

export default function SyncTrainingPeaksAthletes() {

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()

    const { ShowModal } = useContext(ModalContext)

    const userId = useSelector((state) => state.user.id)
    const organizationId = useSelector((state) => state.user.organizationId)
    const trainingChartPresentWeekOfTheYearKey = useSelector((state) => state.athleteProfile.trainingChartPresentWeekOfTheYearKey)

    const [searchTerm, setSearchTerm] = useState('')

    const kinesisAthletes = useGetAthletes()

    const [filteredKinesisAthletes, setFilteredKinesisAthletes] = useState(null)
    const [trainingPeaksAthletes, setTrainingPeaksAthletes] = useState(null)
    const [filteredTrainingPeaksAthletes, setFilteredTrainingPeaksAthletes] = useState(null)
    const [trainingPeaksUserIdToKinesisAthleteIdMap, setTrainingPeaksUserIdToKinesisAthleteIdMap] = useState(null)
    const [savingMapping, setSavingMapping] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const GetTrainingPeaksCoachAthletes = async () => {

        /*
        const dummyData = [
            {
                "Id": 6078064,
                "FirstName": "Example",
                "LastName": "Athlete",
                "Email": "example_athlete@kinesisintegrated.com",
                "TimeZone": "America/Denver",
                "BirthMonth": null,
                "Sex": "m",
                "CoachedBy": 6078062,
                "Weight": null,
                "IsPremium": false,
                "PreferredUnits": "English"
            },
            {
                "Id": 6078063,
                "FirstName": "Example",
                "LastName": "Client",
                "Email": null,
                "TimeZone": "US/Mountain",
                "BirthMonth": null,
                "Sex": null,
                "CoachedBy": 6078062,
                "Weight": null,
                "IsPremium": false,
                "PreferredUnits": "English"
            }
        ]

        setTrainingPeaksAthletes(dummyData)

        return
        */

        const jsonBody = {
            userId: userId
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('CORE', 'GET', '/training_peaks_coach_athletes', jsonBody);

        // console.log(result.data.data)

        if (result.data.status === 'failure') {
            setErrorMessage("Unable to get TrainingPeaks Athletes. Refresh the page or reconnect your TrainingPeaks account to try again.")
            setTrainingPeaksAthletes([])
            return
        }

        setTrainingPeaksAthletes(result.data.data)
    }

    const FilterAthletesBySearchTerm = (item) => {
        const firstName = item.FirstName?.toLowerCase() || ''
        const lastName = item.LastName?.toLowerCase() || ''
        const fullName = `${firstName} ${lastName}`.trim()
        const email = item.Email?.toLowerCase() || ''

        return (
            fullName.includes(searchTerm.toLowerCase()) ||
            email.includes(searchTerm.toLowerCase())
        )
    }

    const GetTrainingPeaksAthleteDetails = (trainingPeaksAthleteId) => {
        if (!trainingPeaksAthleteId) return null
        return trainingPeaksAthletes?.find(trainingPeaksAthlete => trainingPeaksAthlete.Id.toString() === trainingPeaksAthleteId.toString()) || null
    }

    const GetKinesisAthleteDetails = (kinesisAthleteId) => {
        if (!kinesisAthleteId) return null
        return filteredKinesisAthletes?.find(kinesisAthlete => kinesisAthlete.id.toString() === kinesisAthleteId.toString()) || null
    }

    // TrainingPeaks to Kinesis Athlete Mappping functions

    const InitializeTrainingPeaksUserIdToKinesisAthleteIdMap = (defaultValue = 'no-mapping') => {
        const map = {}
        const assignedKinesisAthleteIds = []

        // First loop to attach Kinesis Athlete IDs to the map. Default to null if not found.

        trainingPeaksAthletes.forEach((trainingPeaksAthlete) => {

            const kinesisAthlete = filteredKinesisAthletes.find(kinesisAthlete => kinesisAthlete.trainingPeaksUserId.toString() === trainingPeaksAthlete.Id.toString())

            if (kinesisAthlete) {

                assignedKinesisAthleteIds.push(kinesisAthlete.id.toString()) // Build array of already saved Kinesis Athlete IDs

                map[trainingPeaksAthlete.Id.toString()] = {
                    status: 'assigned',
                    value: kinesisAthlete.id.toString()
                }

            } else {

                map[trainingPeaksAthlete.Id.toString()] = {
                    status: 'unassigned',
                    value: null
                }

            }
        })

        // Second loop to autofill with email matches. Default to 'create-account' if not found.

        for (const key in map) {

            // Skip TrainingPeaks Athlete if it already has a Kinesis Athlete ID

            if (map[key].status === 'assigned') {
                continue
            }

            // If default value is 'no-mapping', set the value to 'no-mapping'

            if (defaultValue === 'no-mapping') {
                map[key].value = defaultValue
                continue
            }

            // If default value is 'create-account', check for email matches

            if (defaultValue === 'create-account') {
                
                const trainingPeaksAthlete = GetTrainingPeaksAthleteDetails(key)
                
                if (trainingPeaksAthlete !== null) {
                    
                    var trainingPeaksEmail = trainingPeaksAthlete.Email || null
                    
                    // console.log(trainingPeaksEmail)
                    
                    if (trainingPeaksEmail !== null && trainingPeaksEmail !== '') {
                        
                        const kinesisAthlete = filteredKinesisAthletes.find(kinesisAthlete => kinesisAthlete.email.toLowerCase() === trainingPeaksEmail.toLowerCase())
                        
                        // console.log(kinesisAthlete)
                        
                        if (
                            kinesisAthlete !== null 
                            && kinesisAthlete !== undefined
                            // Prevent a training peaks athlete from being auto-assigned to a Kinesis athlete if the Kinesis athlete is already mapped
                            && assignedKinesisAthleteIds.includes(kinesisAthlete.id.toString()) === false 
                        ) {
                            map[key].value = kinesisAthlete.id.toString()
                            continue
                        }
                        
                    }
                }

                map[key].value = defaultValue

                continue
            }
            
        }

        // console.log(map)

        setTrainingPeaksUserIdToKinesisAthleteIdMap(map)
    }

    const CreateAllTrainingPeaksUserIdToKinesisAthleteIdMap = () => {
        const new_map = JSON.parse(JSON.stringify(trainingPeaksUserIdToKinesisAthleteIdMap))

        for (const key in new_map) {
            new_map[key].value = 'create-account'
        }

        setTrainingPeaksUserIdToKinesisAthleteIdMap(new_map)
    }

    const ClearAllTrainingPeaksUserIdToKinesisAthleteIdMap = () => {
        const new_map = JSON.parse(JSON.stringify(trainingPeaksUserIdToKinesisAthleteIdMap))

        for (const key in new_map) {
            new_map[key].value = 'no-mapping'
        }

        setTrainingPeaksUserIdToKinesisAthleteIdMap(new_map)
    }

    const UpdateTrainingPeaksToKinesisAthleteMap = (trainingPeaksUserId, value) => {

        const new_map = JSON.parse(JSON.stringify(trainingPeaksUserIdToKinesisAthleteIdMap))

        // Remove the value from the map if it already exists

        for (const key in new_map) {
            if (new_map[key].value === 'no-mapping') {
                continue
            }

            if (new_map[key].value === value) {
                new_map[key].value = 'create-account'
            }
        }

        // Add the new value to the map

        new_map[trainingPeaksUserId].value = value

        // console.log(new_map)

        setTrainingPeaksUserIdToKinesisAthleteIdMap(new_map)
    }

    const SaveMappingToBackend = async () => {
        setSavingMapping(true)
        setErrorMessage('')

        var cleanedMap = {}

        for (const key in trainingPeaksUserIdToKinesisAthleteIdMap) {
            if (
                typeof trainingPeaksUserIdToKinesisAthleteIdMap[key].value === 'string'
                && trainingPeaksUserIdToKinesisAthleteIdMap[key].value !== 'create-account'
                && trainingPeaksUserIdToKinesisAthleteIdMap[key].value !== 'no-mapping'
            ) {
                cleanedMap[key] = trainingPeaksUserIdToKinesisAthleteIdMap[key].value
            }
        }

        const jsonBody = {
            organizationId: organizationId,
            coachId: userId,
            updatedMap: cleanedMap,
            clientDate: moment().local().format('YYYY-MM-DD')
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('CORE', 'POST', '/training_peaks_to_kinesis_user_mappings', jsonBody)

        // console.log(result)

        if (result.data.status === 'failure') {
            setErrorMessage(result.data.message)
            setSavingMapping(false)
            ShowSyncFailureModal()
            return
        }

        setSavingMapping(false)

        // Push Kinesis Workouts to TrainingPeaks. Does nothing if already pushed

        Object.keys(cleanedMap).map(key => {

            const kinesisUserId = cleanedMap[key]
            const trainingPeaksUserId = key

            if (kinesisUserId === 'create-account' || kinesisUserId === 'no-mapping') {
                return;
            }

            const pushJsonBody = {
                userId: kinesisUserId,
                trainingPeaksUserId: trainingPeaksUserId,
                weekOfTheYearKey: trainingChartPresentWeekOfTheYearKey
            }

            // console.log(pushJsonBody)

            WhichWeightAPI('CORE', 'POST', '/training_peaks_kinesis_workout_push', pushJsonBody)

            // V5 (boilerplate) athletes: this week's workouts are pushed (new) or updated on TrainingPeaks. Does nothing for legacy athletes

            const jsonBody2 = {
                userId: kinesisUserId,
                clientDate: moment().local().format('YYYY-MM-DD')
            }

            WhichWeightAPI('PROGRAM', 'POST', '/sync_training_peaks_workouts', jsonBody2)

        })

        // Check if there are any accounts to create

        var createAccountData = []

        for (const key in trainingPeaksUserIdToKinesisAthleteIdMap) {
            if (trainingPeaksUserIdToKinesisAthleteIdMap[key].value === 'create-account') {
                var trainingPeaksAthleteDetails = GetTrainingPeaksAthleteDetails(key)

                var newItem = {
                    trainingPeaksUserId: key,
                    firstName: trainingPeaksAthleteDetails.FirstName,
                    lastName: trainingPeaksAthleteDetails.LastName,
                    email: trainingPeaksAthleteDetails.Email ? trainingPeaksAthleteDetails.Email : ''
                }

                createAccountData.push(newItem)
            }
        }

        if (createAccountData.length > 0) {
            const modalData = {
                key: ModalKey.Action,
                size: 'quarter',
                icon: <CheckIcon className="h-8 w-8 text-white" aria-hidden="true" />,
                titleText: 'Success!',
                bodyText: [
                    'TrainingPeaks athletes have been successfully synced to Kinesis athletes',
                    'You have ' + createAccountData.length + ' new athlete' + (createAccountData.length > 1 ? 's' : '') + ' to create accounts for'
                ],
                buttonText: 'Continue to Account Creation',
                buttonAction: () => { 
                    
                    const props = {
                        state: {
                            createAccountData: createAccountData
                        }
                    }

                    navigate('/onboarding/athletes/create', props) 

                    // For good measure reset the organization state
                    dispatch(resetOrganizationState())

                }
            }
    
            ShowModal(modalData)

            return
        }

        // If there are no accounts to create, navigate to success page with success message
            
        const navigationProps = {
            state: {
                successMessage: 'TrainingPeaks sync has been updated'
            }
        }

        navigate('/success', navigationProps)
    }

    const ShowSyncFailureModal = () => {
        const modalData = {
            key: ModalKey.Action,
            size: 'quarter',
            icon: <ExclamationTriangleIcon className="h-8 w-8 text-white" aria-hidden="true" />,
            titleText: 'Error!',
            bodyText: [
                'Failed to sync TrainingPeaks athletes to Kinesis athletes. Please try again.',
                'If the problem persists, please contact support@kinesisintegrated.com'
            ],
            buttonText: 'Close'
        }

        ShowModal(modalData)
    }

    useEffect(() => { GetTrainingPeaksCoachAthletes() }, [])

    useEffect(() => {
        if (kinesisAthletes !== null) {
            setFilteredKinesisAthletes(
                kinesisAthletes.filter(athlete => 
                    athlete.trainingPeaksMappingAssignerCoachId === userId ||
                    athlete.trainingPeaksMappingAssignerCoachId === null
                )
            )
        }
    }, [kinesisAthletes])

    useEffect(() => {
        if (trainingPeaksAthletes !== null && filteredKinesisAthletes !== null) {

            var defaultValue = searchParams.get('defaultValue') || 'no-mapping'

            if (defaultValue !== 'create-account' && defaultValue !== 'no-mapping') {
                defaultValue = 'no-mapping'
            }

            // console.log(defaultValue)

            InitializeTrainingPeaksUserIdToKinesisAthleteIdMap(defaultValue)
        }
    }, [trainingPeaksAthletes, filteredKinesisAthletes, searchParams])

    useEffect(() => {
        if (trainingPeaksAthletes !== null) {
            setFilteredTrainingPeaksAthletes(trainingPeaksAthletes.filter(FilterAthletesBySearchTerm))
        }
    }, [trainingPeaksAthletes, searchTerm])

    if (
        trainingPeaksAthletes === null
        || filteredTrainingPeaksAthletes === null
        || kinesisAthletes === null
        || filteredKinesisAthletes === null
        || trainingPeaksUserIdToKinesisAthleteIdMap === null
    ) {
        return (
            <ScreenFrame>
                <div className='flex items-center gap-x-2'>
                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight text-center py-1">Sync Athletes From TrainingPeaks</h1>
                </div>

                <div className='h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='h-20 bg-gray-200 rounded-lg animate-pulse' />
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <div className='flex items-center gap-x-2'>
                <div className='space-y-1'>
                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight py-1">Sync Athletes From TrainingPeaks</h1>
                    <p className='text-sm'>
                        Connect TrainingPeaks Athletes to Kinesis Athletes. This will allow athlete calendars to be synced between TrainingPeaks and Kinesis for connected athletes. Connections are auto-filled where emails match between TrainingPeaks and Kinesis.
                    </p>
                </div>
            </div>

            <div className="flex gap-x-4">
                <div className="flex-grow relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>

                    <input
                        type="text"
                        className="w-full py-2 pl-10 pr-3 rounded-md border border-gray-200 bg-white placeholder-gray-500 focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400 shadow-md"
                        placeholder="Enter TrainingPeaks Athlete Name or Email"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                    />
                </div>
            </div>

            {filteredTrainingPeaksAthletes.length === 0 && (
                <ContentFrame>
                    <div className="text-center text-gray-500 py-4">No TrainingPeaks Athletes found</div>
                </ContentFrame>
            )}

            {filteredTrainingPeaksAthletes.length > 0 &&
                <div className='py-4 space-y-8'>

                    {filteredTrainingPeaksAthletes
                    .filter(trainingPeaksAthlete => trainingPeaksUserIdToKinesisAthleteIdMap[trainingPeaksAthlete.Id]?.status === 'unassigned')
                    .length > 0 && (
                        <div className='space-y-4'>
                            <p>Unassigned Athletes <span className='pl-1 text-gray-500'>(Autofilling based on email. Defaulting no matches to 'Create Account')</span></p>

                            {filteredTrainingPeaksAthletes
                            .filter(trainingPeaksAthlete => trainingPeaksUserIdToKinesisAthleteIdMap[trainingPeaksAthlete.Id]?.status === 'unassigned')
                            .map((trainingPeaksAthlete, index) => (
                                <TrainingPeaksToKinesisAthleteMappingListItem
                                    key={index}
                                    trainingPeaksAthlete={trainingPeaksAthlete}
                                    mappedKinesisAthleteUserId={trainingPeaksUserIdToKinesisAthleteIdMap[trainingPeaksAthlete.Id]?.value || null}
                                    kinesisAthletes={filteredKinesisAthletes}
                                    GetKinesisAthleteDetails={GetKinesisAthleteDetails}
                                    UpdateTrainingPeaksToKinesisAthleteMap={UpdateTrainingPeaksToKinesisAthleteMap}
                                />
                            ))}
                        </div>
                    )}
                    
                    {filteredTrainingPeaksAthletes
                    .filter(trainingPeaksAthlete => trainingPeaksUserIdToKinesisAthleteIdMap[trainingPeaksAthlete.Id]?.status === 'assigned')
                    .length > 0 && (
                        <div className='space-y-4'>
                            <p>Assigned Athletes</p>

                            {filteredTrainingPeaksAthletes
                            .filter(trainingPeaksAthlete => trainingPeaksUserIdToKinesisAthleteIdMap[trainingPeaksAthlete.Id]?.status === 'assigned')
                            .map((trainingPeaksAthlete, index) => (
                                <TrainingPeaksToKinesisAthleteMappingListItem
                                    key={index}
                                    trainingPeaksAthlete={trainingPeaksAthlete}
                                    mappedKinesisAthleteUserId={trainingPeaksUserIdToKinesisAthleteIdMap[trainingPeaksAthlete.Id]?.value || null}
                                    kinesisAthletes={filteredKinesisAthletes}
                                    GetKinesisAthleteDetails={GetKinesisAthleteDetails}
                                    UpdateTrainingPeaksToKinesisAthleteMap={UpdateTrainingPeaksToKinesisAthleteMap}
                                />
                            ))}
                        </div>
                    )}
                </div>
            }

            <div className='pt-4 space-y-4'>
                {errorMessage !== '' && (
                    <p className="text-sm text-red-500 font-semibold text-center">{errorMessage}</p>
                )}

                {filteredTrainingPeaksAthletes.length > 0 && (
                    <div className='flex gap-x-4'>
                        <div className='flex-1'>
                            <SolidButton
                                text="Disconnect All"
                                action={ClearAllTrainingPeaksUserIdToKinesisAthleteIdMap}
                                className='shadow-md'
                            />
                        </div>

                        <div className='flex-1'>
                            <SolidButton
                                text="Create Account For All"
                                action={CreateAllTrainingPeaksUserIdToKinesisAthleteIdMap}
                                className='shadow-md'
                            />
                        </div>

                        <div className='flex-1'>
                            <BrandColorButton
                                size="small"
                                text="Save Changes"
                                action={SaveMappingToBackend}
                                isLoading={savingMapping}
                                className='shadow-md'
                            />
                        </div>
                    </div>
                )}
            </div>
        </ScreenFrame>
    )
}